import type { CentreData } from './types';
import { naplanTestPackLabels, naplanTestPackGradesByCourse } from '@/config/naplanTestPacks';

export const pointCookData: CentreData = {
  slug: 'point-cook',
  name: 'Schoolola Point Cook',
  suburb: 'Point Cook',
  state: 'VIC',
  stateName: 'Victoria',
  region: 'Western Melbourne',
  status: 'operational',
  yearLevels: 'Prep - Year 8',
  canonicalPath: '/centres/point-cook/',
  openingBanner: 'Schoolola Point Cook is opening 13th July - book your free assessment now.',
  launchDate: '2026-07-13',
  heroImage: 'https://schoolola.com.au/centres/point-cook-og.jpg',
  address: {
    street: 'Jamieson Way Community Centre, 59 Jamieson Way',
    city: 'Point Cook',
    postcode: '3030',
  },
  contact: {
    phone: '(03) 7047 7576',
    phoneHref: '+61370477576',
    email: 'pc@schoololatutoring.com.au',
  },
  hours: {
    monday: { opens: '15:30', closes: '20:00' },
    tuesday: { opens: '15:30', closes: '20:00' },
    wednesday: { opens: '15:30', closes: '20:00' },
    thursday: { opens: '15:30', closes: '20:00' },
    friday: { opens: '15:30', closes: '20:00' },
    saturday: { opens: '09:00', closes: '13:00' },
    sunday: null,
  },
  heroSubtitle:
    'NAPLAN, selective entry and scholarship preparation by qualified Victorian Curriculum teachers - now taking bookings in Point Cook.',
  breadcrumb: { stateHref: '/states/victoria', regionLabel: 'Western Melbourne' },
  trustStrip: [
    { k: 'To be rated', v: 'Google rating, Point Cook' },
    { k: 'Prep - Year 8', v: 'All year levels supported' },
    { k: 'Victorian Curriculum', v: 'Specialist programmes' },
  ],
  quickBook: {
    featuredCourses: [
      'Selective Entry High School Preparation',
      'SEC Exam Preparation',
      'Foundations Course',
      ...naplanTestPackLabels,
    ],
    eligibleGrades: ['Prep', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6', 'Year 7', 'Year 8'],
    gradesByCourse: {
      ...naplanTestPackGradesByCourse,
      'Selective Entry High School Preparation': ['Year 6', 'Year 7', 'Year 8'],
      'SEC Exam Preparation': ['Year 4', 'Year 5', 'Year 6'],
      'Foundations Course': ['Prep', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6', 'Year 7', 'Year 8'],
    },
    defaultMeetingType: 'in-person',
    allowOnline: true,
    eyebrow: 'Quick Book',
    title: 'Book your free assessment in 60 seconds',
    subtitle: 'A few quick taps and we\u2019ll lock in a real time with our Point Cook team.',
  },
  programs: [
    {
      id: 'foundations',
      title: 'Foundations Course',
      yearPill: { label: 'Prep - Year 8', tone: 'success' },
      tagline: 'Core English and maths · all year levels',
      bullets: ['Phonics, fluency, comprehension', 'Number sense to algebra readiness', 'Writing structure across genres'],
      educationalLevel: 'Prep to Year 8',
      description: 'Core English and mathematics tutoring for Prep through Year 8 students at Schoolola Point Cook, aligned to the Victorian Curriculum.',
    },
    {
      id: 'naplan',
      title: 'NAPLAN Preparation',
      yearPill: { label: 'Yr 3, 5', tone: 'info' },
      tagline: '10-week term aligned to ACARA',
      bullets: ['Reading and writing', 'Numeracy strategies', 'Language conventions'],
      educationalLevel: 'Year 3 and Year 5',
      description: '10-week term NAPLAN preparation course covering reading, writing, language conventions and numeracy, aligned to ACARA test specifications.',
    },
    {
      id: 'selective',
      title: 'Selective Entry High School Preparation',
      yearPill: { label: 'Most popular', tone: 'info' },
      tagline: 'Year 5-8 · Selective entry track',
      bullets: ['Verbal and quantitative reasoning', 'Reading comprehension', 'Mathematical reasoning'],
      featured: 'popular',
      educationalLevel: 'Year 4 to Year 6',
      description: 'Selective entry high school preparation covering verbal reasoning, quantitative reasoning, reading comprehension and mathematical reasoning for the Victorian SEAL test pathway.',
    },
    {
      id: 'sec',
      title: 'SEC Scholarship Exam Course',
      yearPill: { label: 'Yr 4-6', tone: 'info' },
      tagline: 'ACER and Edutest scholarship preparation',
      bullets: ['SEAL/SEALP training', 'Strategic exam techniques', 'Practice test cycles'],
      educationalLevel: 'Year 4 to Year 6',
      description: 'Schoolola SEC programmes - SEC TMRW (booklet-based) and the SEC Scholarship Exam Course - preparing students primarily for ACER and Edutest scholarship exams, with SEAL/SEALP training and structured practice test cycles.',
      prerequisites: 'Basic literacy and numeracy at year-level expectations.',
    },
  ],
  differentiators: [
    {
      title: 'Victorian Curriculum specialists',
      body: "Our team has analysed the curriculum in depth. Lessons map to what your child's school actually teaches, not generic worksheets.",
      icon: 'BookOpen',
    },
    {
      title: 'Measurable progress reports',
      body: 'Quarterly assessments tracked against ACARA bands. Parents see exactly where their child sits.',
      icon: 'LineChart',
    },
    {
      title: 'Small group ratios',
      body: 'Maximum 6 students per tutor. Enough peer dynamics, enough individual attention.',
      icon: 'Users',
    },
    {
      title: 'Selective and scholarship track record',
      body: 'Network students placed at Suzanne Cory, Mac.Robertson Girls, Melbourne High and Nossal High.',
      icon: 'Trophy',
    },
  ],
  faqs: [
    {
      question: 'What year levels do you tutor in Point Cook?',
      answer: 'Prep through Year 8. Foundations covers all year levels in English and maths; NAPLAN, selective entry and scholarship programs target specific testing years.',
    },
    {
      question: 'When can my child start?',
      answer: 'The Point Cook centre is taking bookings now ahead of opening. Most families start with a free assessment, then begin classes in the opening cohort.',
    },
    {
      question: 'How does NAPLAN preparation work?',
      answer: '10-week term covering reading, writing, language conventions and numeracy. Aligned to ACARA test specifications for Years 3, 5 and 7.',
    },
    {
      question: 'Which selective schools do Point Cook students target?',
      answer: 'Suzanne Cory (closest), Mac.Robertson Girls, Melbourne High and Nossal High. Our Selective Entry programme covers all four entry tests, with the Year 8 SEAL test as the entry point.',
    },
    {
      question: 'What does the free assessment include?',
      answer: 'A full assessment, a 1:1 parent consultation, and a written learning plan with recommended programme.',
    },
    {
      question: 'Do you offer online tutoring?',
      answer: 'Yes. Online tutoring is available for families who prefer remote sessions or live further from the centre.',
    },
  ],
};
